'use client';

import { cn, formatDate, formatTruncatedText } from '@/helpers/common';
import { TJobListElement } from '@/ts/jobs';
import { getIndustryTitle, getOccupationTitle } from '@/helpers/taxonomy';
import React from 'react';
import { ROUTES } from '@/constants/routes/default';
import Link from 'next/link';
import { STUB_DEFAULT_TEXTS, STUB_ERROR_TEXTS } from '@/constants/jc/default';
import Tooltip from '../Tooltip';
import StubJC from './stubs/StubJC';
import NewTabHint from '@/components/common/new-tab-hint';

type HomepageJobsJCProps = {
  jobs: TJobListElement[];
  isError?: boolean;
  withCreateProfile?: boolean;
  newTab?: boolean;
};

const CHIP_LABEL_MAX_LENGTH = 40;

const CreateProfileBlock = () => {
  return (
    <li
      key={'create-profile'}
      className="flex items-center justify-between gap-3 rounded-xl p-5 bg-jc-firefly"
    >
      <div>
        <p className="text-white text-lg font-semibold">Tired of running searches?</p>
        <p className="text-jc-botticelli text-sm mt-2 max-w-125">
          Rank the roles you&apos;d take once, and matches like these arrive on their own.
        </p>
      </div>
      <Link
        className={cn(
          'default-transition second-font tracking-wider',
          'text-jc-firefly rounded-md px-3 py-2 bg-jc-my-sin text-sm font-semibold text-center hover:bg-jc-tangerine',
        )}
        href={ROUTES.signup}
      >
        CREATE PROFILE
      </Link>
    </li>
  );
};

const JobsListJC = ({
  jobs,
  isError = false,
  withCreateProfile = false,
  newTab = false,
}: HomepageJobsJCProps) => {
  if (!jobs.length) {
    return (
      <StubJC
        texts={isError ? STUB_ERROR_TEXTS : STUB_DEFAULT_TEXTS}
        elementClassName={{ mainClassName: 'mt-14' }}
      />
    );
  }

  return (
    <ul className="mt-8 flex flex-col gap-3" id="jobs-list">
      {jobs.map((job, index) => {
        const locationLabel = (() => {
          const city = job.city?.trim();
          const state = job.state?.trim();

          if (city && state) return `${city}, ${state}`;
          if (city || state) return city || state || '';
          if (job.isRemote) return 'Remote';

          return '';
        })();

        const dateLabel = formatDate({
          dateValue: job.createdAt,
          variant: 4,
        });
        const occupationTitle = getOccupationTitle(job.occ_1);
        const industryTitle = getIndustryTitle(job.ind_6_1);
        const chips = [
          occupationTitle
            ? {
                key: 'occupation',
                label: occupationTitle,
                displayLabel: formatTruncatedText({
                  text: occupationTitle,
                  maxLength: CHIP_LABEL_MAX_LENGTH,
                }),
              }
            : null,
          industryTitle
            ? {
                key: 'industry',
                label: industryTitle,
                displayLabel: formatTruncatedText({
                  text: industryTitle,
                  maxLength: CHIP_LABEL_MAX_LENGTH,
                }),
              }
            : null,
          job.salary?.raw
            ? {
                key: 'salary',
                label: job.salary.raw,
                displayLabel: job.salary.raw,
              }
            : null,
        ].filter((chip): chip is { key: string; label: string; displayLabel: string } =>
          Boolean(chip),
        );

        return (
          <React.Fragment key={job.id}>
            <li key={job.id} className="block rounded-xl bg-white jc-shadow3 p-4">
              <div className="flex items-start justify-between gap-4">
                <a
                  href={`${ROUTES.jobs}/${job.id}`}
                  target={newTab ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="block font-semibold text-jc-denim no-underline hover:underline"
                >
                  {job.title}
                  {newTab && <NewTabHint />}
                </a>
                <p
                  className={cn(
                    'shrink-0 text-xs mt-1 text-jc-trout second-font tracking-wider',
                    (dateLabel === 'today' || dateLabel === 'yesterday') && 'uppercase',
                  )}
                >
                  {dateLabel}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-1.5">
                {job.company && (
                  <span className="font-semibold capitalize text-jc-firefly">{job.company}</span>
                )}
                {locationLabel && <span className="text-jc-trout">{locationLabel}</span>}
              </div>
              {chips.length > 0 && (
                <div className="mt-3 flex max-w-full flex-wrap gap-2">
                  <div className="flex min-w-0 max-w-full shrink items-center gap-2">
                    {chips.slice(0, 2).map((chip) =>
                      chip.key === 'salary' ? (
                        <span
                          key={chip.key}
                          className="min-w-0 max-w-full shrink overflow-hidden rounded-sm bg-jc-narvik px-1 py-1 text-[11px] uppercase tracking-widest text-jc-eucalyptus second-font"
                        >
                          <span className="block truncate">{chip.displayLabel}</span>
                        </span>
                      ) : (
                        <Tooltip
                          key={chip.key}
                          className="max-w-70 whitespace-normal wrap-break-word rounded-md"
                          tooltip={chip.label}
                        >
                          <span className="min-w-0 max-w-full shrink overflow-hidden rounded-sm bg-jc-catskill-white px-1 py-1 text-[11px] uppercase tracking-widest text-jc-trout second-font">
                            <span className="block truncate">{chip.displayLabel}</span>
                          </span>
                        </Tooltip>
                      ),
                    )}
                  </div>
                  {chips[2]?.key === 'salary' && (
                    <span className="min-w-0 max-w-full shrink overflow-hidden rounded-sm bg-jc-narvik px-1 py-1 text-[11px] uppercase tracking-widest text-jc-eucalyptus second-font">
                      <span className="block truncate">{chips[2].displayLabel}</span>
                    </span>
                  )}
                  {chips[2] && chips[2].key !== 'salary' && (
                    <Tooltip
                      key={chips[2].key}
                      className="max-w-70 whitespace-normal wrap-break-word rounded-md"
                      tooltip={chips[2].label}
                    >
                      <span className="min-w-0 max-w-full shrink overflow-hidden rounded-sm bg-jc-catskill-white px-1 py-1 text-[11px] uppercase tracking-widest text-jc-trout second-font">
                        <span className="block truncate">{chips[2].displayLabel}</span>
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
            </li>
            {withCreateProfile && index === 4 && <CreateProfileBlock />}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default JobsListJC;
